import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/components/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import ReveliaePage from '@/pages/ReveliaePage';
import HomePage from '@/pages/HomePage';
import ScanPage from '@/pages/ScanPage';
import ResultsPage from '@/pages/ResultsPage';
import HistoryPage from '@/pages/HistoryPage';
import KnowledgePage from '@/pages/KnowledgePage';
import ChatPage from '@/pages/ChatPage';
import ChatGeneralPage from '@/pages/ChatGeneralPage';
import TutorialPage from '@/pages/TutorialPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import ComparePage from '@/pages/ComparePage';
import FavoritesPage from '@/pages/FavoritesPage';
import RemindersPage from '@/pages/RemindersPage';
import QuizPage from '@/pages/QuizPage';
import ProfilePage from '@/pages/ProfilePage';
// NUMEROLOGIA Pages
import NumerologiaHomePage from '@/pages/NumerologiaHomePage';
import NumerologiaThemePage from '@/pages/NumerologiaThemePage';
import NumerologiaCyclesPage from '@/pages/NumerologiaCyclesPage';
import NumerologiaDailyPage from '@/pages/NumerologiaDailyPage';
import NumerologiaKarmaPage from '@/pages/NumerologiaKarmaPage';
import NumerologiaHistoryPage from '@/pages/NumerologiaHistoryPage';
import NumerologiaAlertsPage from '@/pages/NumerologiaAlertsPage';
import NumerologiaQuizPage from '@/pages/NumerologiaQuizPage';
import NumerologiaKnowledgePage from '@/pages/NumerologiaKnowledgePage';
import NumerologiaChatPage from '@/pages/NumerologiaChatPage';
// ASTROZODIAC Pages
import AstrozodiacHomePage from '@/pages/AstrozodiacHomePage';
import AstrozodiacBigThreePage from '@/pages/AstrozodiacBigThreePage';
import AstrozodiacPlanetsPage from '@/pages/AstrozodiacPlanetsPage';
import AstrozodiacHousesPage from '@/pages/AstrozodiacHousesPage';
import AstrozodiacAspectsPage from '@/pages/AstrozodiacAspectsPage';
import AstrozodiacTransitsPage from '@/pages/AstrozodiacTransitsPage';
import AstrozodiacDecisionPage from '@/pages/AstrozodiacDecisionPage';
import AstrozodiacQuizPage from '@/pages/AstrozodiacQuizPage';
import AstrozodiacKnowledgePage from '@/pages/AstrozodiacKnowledgePage';
import AstrozodiacChatPage from '@/pages/AstrozodiacChatPage';
import AstrozodiacHoroscopePage from '@/pages/AstrozodiacHoroscopePage';
// MYSTERIAE Pages
import MysteriaeHomePage from '@/pages/MysteriaeHomePage';
import MysteriaeArcanesPage from '@/pages/MysteriaeArcanesPage';
import MysteriaeCardDetailPage from '@/pages/MysteriaeCardDetailPage';
import MysteriaeRandomPage from '@/pages/MysteriaeRandomPage';
import MysteriaeDrawPage from '@/pages/MysteriaeDrawPage';
import MysteriaeHistoryPage from '@/pages/MysteriaeHistoryPage';
import MysteriaeQuizPage from '@/pages/MysteriaeQuizPage';
import MysteriaeKnowledgePage from '@/pages/MysteriaeKnowledgePage';
import MysteriaeChatPage from '@/pages/MysteriaeChatPage';
// PREMIUM Pages
import SubscriptionPage from '@/pages/SubscriptionPage';
import SubscriptionSuccessPage from '@/pages/SubscriptionSuccessPage';
// TRANSVERSAL ANALYSIS
import TransversalAnalysisPage from '@/pages/TransversalAnalysisPage';
// GUEST / FUNNEL Pages
import GuestScanPage from '@/pages/GuestScanPage';
import GuestResultsPage from '@/pages/GuestResultsPage';
// LEGAL Pages
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/legal/CookiePolicyPage';
import TermsOfSalePage from '@/pages/legal/TermsOfSalePage';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    let storedSession = localStorage.getItem('lumina_session_id');
    if (!storedSession) {
      storedSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('lumina_session_id', storedSession);
    }
    setSessionId(storedSession);
  }, []);

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      <Route path="/" element={<ReveliaePage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/scan" element={
        <ProtectedRoute>
          <ScanPage sessionId={sessionId} />
        </ProtectedRoute>
      } />
      <Route path="/results/:readingId" element={<ResultsPage />} />
      <Route path="/history" element={<HistoryPage sessionId={sessionId} />} />
      <Route path="/knowledge" element={
        <ProtectedRoute>
          <KnowledgePage />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/chat-general" element={<ChatGeneralPage />} />
      <Route path="/tutorial" element={<TutorialPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/favorites" element={<FavoritesPage sessionId={sessionId} />} />
      <Route path="/reminders" element={<RemindersPage sessionId={sessionId} />} />
      {/* NUMEROLOGIA Routes */}
      <Route path="/numerologia" element={<NumerologiaHomePage />} />
      <Route path="/numerologia/theme" element={<NumerologiaThemePage />} />
      <Route path="/numerologia/cycles" element={<NumerologiaCyclesPage />} />
      <Route path="/numerologia/daily" element={<NumerologiaDailyPage />} />
      <Route path="/numerologia/karma" element={<NumerologiaKarmaPage />} />
      <Route path="/numerologia/history" element={<NumerologiaHistoryPage />} />
      <Route path="/numerologia/alerts" element={<NumerologiaAlertsPage />} />
      <Route path="/numerologia/quiz" element={<NumerologiaQuizPage />} />
      <Route path="/numerologia/knowledge" element={<NumerologiaKnowledgePage />} />
      <Route path="/numerologia/chat" element={<NumerologiaChatPage />} />
      {/* ASTROZODIAC Routes */}
      <Route path="/astrozodiac" element={<AstrozodiacHomePage />} />
      <Route path="/astrozodiac/big-three" element={<AstrozodiacBigThreePage />} />
      <Route path="/astrozodiac/planets" element={<AstrozodiacPlanetsPage />} />
      <Route path="/astrozodiac/houses" element={<AstrozodiacHousesPage />} />
      <Route path="/astrozodiac/aspects" element={<AstrozodiacAspectsPage />} />
      <Route path="/astrozodiac/transits" element={<AstrozodiacTransitsPage />} />
      <Route path="/astrozodiac/decision" element={<AstrozodiacDecisionPage />} />
      <Route path="/astrozodiac/quiz" element={<AstrozodiacQuizPage />} />
      <Route path="/astrozodiac/knowledge" element={<AstrozodiacKnowledgePage />} />
      <Route path="/astrozodiac/chat" element={<AstrozodiacChatPage />} />
      <Route path="/astrozodiac/horoscope" element={<AstrozodiacHoroscopePage />} />
      {/* MYSTERIAE Routes */}
      <Route path="/mysteriae" element={<MysteriaeHomePage />} />
      <Route path="/mysteriae/arcanes" element={<MysteriaeArcanesPage />} />
      <Route path="/mysteriae/arcanes/:id" element={<MysteriaeCardDetailPage />} />
      <Route path="/mysteriae/random" element={<MysteriaeRandomPage />} />
      <Route path="/mysteriae/draw" element={<MysteriaeDrawPage />} />
      <Route path="/mysteriae/history" element={<MysteriaeHistoryPage />} />
      <Route path="/mysteriae/quiz" element={<MysteriaeQuizPage />} />
      <Route path="/mysteriae/knowledge" element={<MysteriaeKnowledgePage />} />
      <Route path="/mysteriae/chat" element={<MysteriaeChatPage />} />
      {/* PREMIUM Routes */}
      <Route path="/premium" element={<SubscriptionPage />} />
      <Route path="/premium/success" element={<SubscriptionSuccessPage />} />
      {/* TRANSVERSAL ANALYSIS */}
      <Route path="/analysis/transversal" element={<TransversalAnalysisPage />} />
      {/* GUEST / FUNNEL Routes */}
      <Route path="/try" element={<GuestScanPage />} />
      <Route path="/try/results" element={<GuestResultsPage />} />
      {/* LEGAL Routes */}
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/cookies" element={<CookiePolicyPage />} />
      <Route path="/legal/terms" element={<TermsOfSalePage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
